/**
 * The page-guide data contract (spec §9.5, port of the MGA help system).
 *
 * Pure data and pure functions — no React, no component library, no router. Node scripts
 * (`scripts/help-check.mjs`, `scripts/build-user-manual.mjs`) and the browser audit import this
 * file and the registry directly, so a UI import sneaking in here breaks all three.
 */

/**
 * The role ladder, mirroring what the UI hides:
 *
 * | `HelpRole` | Who                                                                          |
 * |------------|------------------------------------------------------------------------------|
 * | `user`     | anyone registered — what they may do in a project depends on its grants     |
 * | `admin`    | `admin` or `superadmin`: the Administration screens, new projects/packages  |
 *
 * Two rungs, because the scope roles (owner, translator, developer, viewer) are not a ladder — a
 * translator may edit values a developer may not, and the other way round for keys — and they are
 * per project. A step about a control only some scope roles get says so in its body instead;
 * `minRole` is for what the UI hides from everybody below the admin tier.
 */
export type HelpRole = 'user' | 'admin';

/**
 * The languages a guide may be written in, EN being the one it is always written in.
 *
 * Declared here rather than imported from a shared package so this file keeps the promise in its
 * header — a Node script, a Playwright spec and the manual generator all import it with no bundler
 * and no workspace resolution. The codes are BCP 47 like every locale in Glossa: German is written
 * for Austria (`de-AT`), Serbian in Latin script (`sr-Latn-RS`) — the readers sit in Vienna and
 * Belgrade, and a code that says so leaves room for another variant without a rename.
 */
export const HELP_LANGUAGES = ['en', 'de-AT', 'sr-Latn-RS'] as const;
export type HelpLanguage = (typeof HELP_LANGUAGES)[number];
/** EN is the source text, never a translation — hence `i18n` covers only the others. */
export const HELP_BASE_LANGUAGE: HelpLanguage = 'en';
export type HelpTranslatedLanguage = Exclude<HelpLanguage, 'en'>;

/** The short label of a language on the drawer's toggle and in the manual's file name. */
export const HELP_LANGUAGE_LABELS: Record<HelpLanguage, string> = {
  en: 'EN',
  'de-AT': 'DE',
  'sr-Latn-RS': 'SR',
};

/**
 * Translations, per field and optional at every level.
 *
 * Per FIELD rather than per step, because a half-translated guide is the normal state of one:
 * a title that reads fine in a translated language next to a body nobody has got to yet is more
 * useful than an all-or-nothing switch that keeps both in English. Anything unset falls back to
 * the EN text, and the surface says so rather than passing the fallback off as a translation.
 *
 * Selectors are NOT translatable and never will be: the anchor is the contract between guide
 * and page, shared across languages — which is why `help:check`, `help:impact` and the hooks
 * are language-blind by construction.
 */
export type HelpStepI18n = Partial<Record<HelpTranslatedLanguage, { title?: string; body?: string }>>;

export type PageHelpI18n = Partial<
  Record<
    HelpTranslatedLanguage,
    {
      title?: string;
      intro?: string;
      /** EN section name → translated heading. Keyed by the EN name, which stays the id. */
      sections?: Record<string, string>;
      /** `HelpStep.context` key → the translated "how to get there" line. */
      contexts?: Record<string, string>;
      tips?: string[];
    }
  >
>;

/**
 * What the signed-in user is allowed to do — from the auth store, passed in as plain flags so
 * `help/` stays importable with no providers (a test or a Node script has no auth context).
 */
export interface HelpViewer {
  /** `admin` or `superadmin` — the tier `isAdminAtom` reports. */
  isAdmin: boolean;
}

export interface HelpStep {
  /**
   * Always a `[data-help="…"]` attribute selector, never a class or text match.
   * The attribute is the contract between the guide and the page; classes and labels
   * change on every restyle.
   */
  selector: string;
  /** Short action phrase — "Filter the list by status", not "Filter chips". */
  title: string;
  /** 1–4 plain sentences: what it does, when to use it, what happens next. */
  body: string;
  /**
   * Groups the card in the drawer. Order comes from `PageHelp.sections`; a step with an
   * unlisted (or missing) section falls into a trailing "More" group.
   */
  section?: string;
  /** Hide the step from lower roles — only when the UI hides the control. Default: `user`. */
  minRole?: HelpRole;
  /**
   * The element only exists after a UI action — a key in `PageHelp.contexts`, whose value
   * tells the reader how to get there ("Click New key…"). The tour skips these while
   * they are off screen; the drawer still lists them, with the instruction attached.
   */
  context?: string;
  /** The element needs data to exist (a non-empty list, an empty list, a filled row). */
  optional?: boolean;
  /** Translations of `title` / `body`. EN is this object's own fields — see `HelpStepI18n`. */
  i18n?: HelpStepI18n;
}

export interface PageHelp {
  title: string;
  /** 1–3 sentences: what the page is for and where it sits in the workflow. */
  intro: string;
  /** Display order for `HelpStep.section`. Sections not listed here render last. */
  sections?: string[];
  /** `HelpStep.context` key → how the reader reveals that part of the UI. */
  contexts?: Record<string, string>;
  /** Cross-cutting notes that belong to no single control. */
  tips?: string[];
  steps: HelpStep[];
  /** Translations of the page-level prose. Steps carry their own — see `PageHelpI18n`. */
  i18n?: PageHelpI18n;
}

/**
 * The prose to show, in the reader's language, with EN standing in per field where nothing
 * has been written yet.
 *
 * `translated` is false when ANY displayed field fell back, so a card can say "shown in
 * English" instead of quietly mixing the two — which reads to a reader of the translated
 * language as a bug in the app rather than as a gap in the manual.
 */
export interface LocalizedText<T> {
  value: T;
  translated: boolean;
}

const pick = <T>(translation: T | undefined, base: T): LocalizedText<T> =>
  translation === undefined || translation === null || translation === ''
    ? { value: base, translated: false }
    : { value: translation, translated: true };

export const localizedStep = (
  step: HelpStep,
  language: HelpLanguage,
): { title: string; body: string; translated: boolean } => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: step.title, body: step.body, translated: true };
  }
  const source = step.i18n?.[language];
  const title = pick(source?.title, step.title);
  const body = pick(source?.body, step.body);
  return { title: title.value, body: body.value, translated: title.translated && body.translated };
};

export const localizedPage = (
  help: PageHelp,
  language: HelpLanguage,
): { title: string; intro: string; tips: string[] | undefined; translated: boolean } => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: help.title, intro: help.intro, tips: help.tips, translated: true };
  }
  const source = help.i18n?.[language];
  const title = pick(source?.title, help.title);
  const intro = pick(source?.intro, help.intro);
  const tips = pick(source?.tips?.length ? source.tips : undefined, help.tips);
  return {
    title: title.value,
    intro: intro.value,
    tips: tips.value,
    translated: title.translated && intro.translated && (!help.tips?.length || tips.translated),
  };
};

/** A section heading is an id in EN and a label in every language, hence keyed by the EN name. */
export const localizedSection = (help: PageHelp, section: string, language: HelpLanguage): string =>
  language === HELP_BASE_LANGUAGE
    ? section
    : (help.i18n?.[language]?.sections?.[section] ?? section);

export const localizedContext = (
  help: PageHelp,
  key: string,
  language: HelpLanguage,
): string | undefined =>
  (language === HELP_BASE_LANGUAGE
    ? undefined
    : help.i18n?.[language]?.contexts?.[key]) ?? help.contexts?.[key];

/** The whole `contexts` map in one language, for a surface that is handed the record itself. */
export const localizedContexts = (
  help: PageHelp,
  language: HelpLanguage,
): Record<string, string> | undefined => {
  if (!help.contexts) return undefined;
  if (language === HELP_BASE_LANGUAGE) return help.contexts;
  const translated = help.i18n?.[language]?.contexts;
  if (!translated) return help.contexts;
  return Object.fromEntries(
    Object.entries(help.contexts).map(([key, value]) => [key, translated[key] ?? value]),
  );
};

export const ROLE_RANK: Record<HelpRole, number> = {
  user: 0,
  admin: 1,
};

export const rankForViewer = (viewer: HelpViewer): number => (viewer.isAdmin ? 1 : 0);

export const stepVisibleForRole = (step: HelpStep, viewer: HelpViewer): boolean =>
  rankForViewer(viewer) >= ROLE_RANK[step.minRole ?? 'user'];

/** The role badge shown on a card, or `null` when the step is visible to everyone. */
export const roleBadgeLabel = (step: HelpStep): string | null =>
  step.minRole === 'admin' ? 'Admin' : null;

/**
 * Steps grouped for the drawer, in `PageHelp.sections` order. Sections present on steps
 * but missing from that list keep their first-seen order and land after the declared
 * ones, so adding a step without touching `sections` degrades to "shown last" rather
 * than "silently dropped".
 *
 * Takes the section list rather than the whole `PageHelp` because the tour groups too —
 * and the tour only ever holds the on-screen subset of the steps, never the guide.
 */
export const groupStepsBySection = (
  sections: string[] | undefined,
  steps: HelpStep[],
): { section: string; steps: HelpStep[] }[] => {
  const FALLBACK = 'More';
  const buckets = new Map<string, HelpStep[]>();
  for (const section of sections ?? []) buckets.set(section, []);
  for (const step of steps) {
    const key = step.section ?? FALLBACK;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(step);
    else buckets.set(key, [step]);
  }
  return [...buckets.entries()]
    .filter(([, group]) => group.length > 0)
    .map(([section, group]) => ({ section, steps: group }));
};

/**
 * The anchor name behind a step's selector — `[data-help="keys-grid"]` → `keys-grid`.
 *
 * The reverse of how a step is written, and what click-to-explain runs on: it starts from an
 * element the user pointed at, reads its attribute, and has to find the step that documents it.
 * Returns null for anything that is not the plain attribute selector the guides are limited to
 * (`registry.test.ts` enforces the shape), so a hand-written oddity fails to resolve rather than
 * resolving to the wrong step.
 */
export const anchorNameOf = (selector: string): string | null =>
  /^\[data-help="([^"]+)"\]$/.exec(selector)?.[1] ?? null;

/**
 * Free-text match for the two "take me straight there" surfaces — the drawer's filter box and
 * the tour's jump list.
 *
 * Every whitespace-separated term must appear somewhere in the step, in any order, so "fallback"
 * finds the step whose BODY explains fallback values. Searching the body and not just the title is
 * the point: a user who cannot name the field is exactly the user who needs the search.
 */
export const stepMatchesQuery = (step: HelpStep, query: string): boolean => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  // EVERY language's text, not the displayed one: the app's own UI is English, so a German or
  // Serbian reader knows the English labels too — and a step nobody has translated yet must not
  // vanish from the search the moment another language is selected.
  const translations = Object.values(step.i18n ?? {})
    .map((t) => `${t?.title ?? ''} ${t?.body ?? ''}`)
    .join(' ');
  const haystack =
    `${step.title} ${step.body} ${step.section ?? ''} ${step.selector} ${translations}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};
